/**
 * Análises › Filtros (D528; research doc 05 §4): the pure view-model of the filter builder - what a condition IS on the client, which
 * conditions are filled enough to ask, how a builder state becomes the request fields (`filters`, `anyOf`, `having`) and back, how a pasted list
 * of ids is read, and how a state is tidied for a person who may not see what it names. The screens only draw what this module decides.
 *
 * The server is the authority on everything that matters (the planner validates every condition and answers with a stable reason code); the
 * rules here only keep the screen from asking what is certain to be refused, and from losing what the operator typed.
 */
import {
  FILTER_LIMITS,
  type FilterOp,
  type HavingOp,
  type QueryFilter,
  type QueryHaving,
} from '@vello/analytics-semantic';
import { type AnalyticsBiCatalogView } from '@vello/api-client';

export interface BiFilterSpec {
  readonly dimension: string;
  readonly op: FilterOp;
  readonly values: readonly string[];
  /** A money bound's ISO 4217 currency. */
  readonly currency?: string;
}

export interface BiHavingSpec {
  readonly metric: string;
  readonly op: HavingOp;
  readonly values: readonly string[];
  readonly currency?: string;
}

/** The whole builder: conditions that all apply, OR groups of conditions (one level), and conditions on metric values. */
export interface BiFilterState {
  readonly filters: readonly BiFilterSpec[];
  readonly anyOf: readonly (readonly BiFilterSpec[])[];
  readonly having: readonly BiHavingSpec[];
}

export const EMPTY_FILTER_STATE: BiFilterState = { filters: [], anyOf: [], having: [] };

// ───────────────────────────── operators ─────────────────────────────

export type OpKind = 'set' | 'text' | 'one' | 'between' | 'none';

/** How an operator takes its value(s): a set of values, one text, one bound, two bounds, none. */
export function opKind(op: FilterOp): OpKind {
  switch (op) {
    case 'eq':
    case 'in':
    case 'not_in':
      return 'set';
    case 'contains':
    case 'starts_with':
      return 'text';
    case 'gte':
    case 'lte':
    case 'gt':
    case 'lt':
      return 'one';
    case 'between':
      return 'between';
    case 'is_null':
    case 'not_null':
      return 'none';
  }
}

/** The operators of a dimension, as the SERVER's catalog says (a dimension the catalog does not list offers none). */
export function operatorsOf(catalog: Pick<AnalyticsBiCatalogView, 'dimensions'>, dimension: string): readonly FilterOp[] {
  return catalog.dimensions.find((d) => d.id === dimension)?.operators ?? [];
}

/** The operator a new condition on this dimension starts with: "is one of" where the dimension takes it, else a range, else the first the server offers. */
export function defaultOp(operators: readonly FilterOp[]): FilterOp {
  return operators.includes('in') ? 'in' : operators.includes('between') ? 'between' : (operators[0] ?? 'in');
}

/** Include / exclude is the choice between `in` and `not_in`; the rest of the operators have no opposite on this toggle. */
export function toggledOp(op: FilterOp): FilterOp | undefined {
  return op === 'in' || op === 'eq' ? 'not_in' : op === 'not_in' ? 'in' : undefined;
}

// ───────────────────────────── conditions ─────────────────────────────

const clean = (values: readonly string[]): string[] => values.map((v) => v.trim()).filter((v) => v !== '');

/** Does the condition have everything its operator needs? An unfilled one is kept on screen but never sent. */
export function isFilled(spec: BiFilterSpec, moneyDimension = false): boolean {
  const values = clean(spec.values);
  const kind = opKind(spec.op);
  if (kind === 'none') return true;
  if (kind === 'between') return values.length === 2;
  if (kind === 'set') return values.length >= 1 && values.length <= FILTER_LIMITS.maxValues && (spec.op !== 'eq' || values.length === 1);
  if (moneyDimension && (spec.currency === undefined || spec.currency === '')) return false;
  return values.length === 1;
}

export function isHavingFilled(spec: BiHavingSpec, moneyMetric = false): boolean {
  const values = clean(spec.values);
  if (values.length !== (spec.op === 'between' ? 2 : 1)) return false;
  return !moneyMetric || (spec.currency !== undefined && spec.currency !== '');
}

/** The wire form of a condition (values trimmed, an operator with no value sends none). */
export function toQueryFilter(spec: BiFilterSpec): QueryFilter {
  const kind = opKind(spec.op);
  return {
    dimension: spec.dimension,
    op: spec.op,
    values: kind === 'none' ? [] : clean(spec.values),
    ...(spec.currency !== undefined && spec.currency !== '' && kind !== 'none' ? { currency: spec.currency } : {}),
  };
}

export function toQueryHaving(spec: BiHavingSpec): QueryHaving {
  return { metric: spec.metric, op: spec.op, values: clean(spec.values), ...(spec.currency !== undefined && spec.currency !== '' ? { currency: spec.currency } : {}) };
}

/** What the screen asks: only the filled conditions; an OR group left with none is dropped. */
export function activeFilterState(state: BiFilterState, catalog?: Pick<AnalyticsBiCatalogView, 'dimensions' | 'metrics'>): BiFilterState {
  const money = (d: string): boolean => catalog?.dimensions.find((x) => x.id === d)?.valueType === 'money';
  const moneyMetric = (m: string): boolean => catalog?.metrics.find((x) => x.id === m)?.unit === 'money';
  return {
    filters: state.filters.filter((f) => isFilled(f, money(f.dimension))),
    anyOf: state.anyOf.map((g) => g.filter((f) => isFilled(f, money(f.dimension)))).filter((g) => g.length > 0),
    having: state.having.filter((h) => isHavingFilled(h, moneyMetric(h.metric))),
  };
}

export interface FilterRequestParts {
  readonly filters?: QueryFilter[];
  readonly anyOf?: QueryFilter[][];
  readonly having?: QueryHaving[];
}

/** The request fields of a (filled) state; absent where empty. */
export function filterRequestParts(state: BiFilterState): FilterRequestParts {
  return {
    ...(state.filters.length > 0 ? { filters: state.filters.map(toQueryFilter) } : {}),
    ...(state.anyOf.length > 0 ? { anyOf: state.anyOf.map((g) => g.map(toQueryFilter)) } : {}),
    ...(state.having.length > 0 ? { having: state.having.map(toQueryHaving) } : {}),
  };
}

const specOf = (f: QueryFilter): BiFilterSpec => ({ dimension: f.dimension, op: f.op ?? 'in', values: [...f.values], ...(f.currency !== undefined ? { currency: f.currency } : {}) });

/** The builder state of a request's (or a stored state's) conditions. */
export function stateFromParts(parts: { readonly filters?: readonly QueryFilter[]; readonly anyOf?: readonly (readonly QueryFilter[])[]; readonly having?: readonly QueryHaving[] }): BiFilterState {
  return {
    filters: (parts.filters ?? []).map(specOf),
    anyOf: (parts.anyOf ?? []).map((g) => g.map(specOf)),
    having: (parts.having ?? []).map((h) => ({ metric: h.metric, op: h.op, values: [...h.values], ...(h.currency !== undefined ? { currency: h.currency } : {}) })),
  };
}

export function conditionCount(state: BiFilterState): number {
  return state.filters.length + state.anyOf.reduce((n, g) => n + g.length, 0);
}

/** Can another condition be added without crossing the server's cap? */
export function canAddCondition(state: BiFilterState): boolean {
  return conditionCount(state) < FILTER_LIMITS.maxConditions;
}

// ───────────────────────────── editing (immutable) ─────────────────────────────

/** Where a condition lives: the AND list (`group` undefined) or one OR group. */
export interface ConditionAt {
  readonly group?: number;
  readonly index: number;
}

export function addCondition(state: BiFilterState, spec: BiFilterSpec, group?: number): BiFilterState {
  if (!canAddCondition(state)) return state;
  if (group === undefined) return { ...state, filters: [...state.filters, spec] };
  if (group === state.anyOf.length) {
    // a new OR group: the group count is capped like the server's
    return state.anyOf.length >= FILTER_LIMITS.maxGroups ? state : { ...state, anyOf: [...state.anyOf, [spec]] };
  }
  return { ...state, anyOf: state.anyOf.map((g, i) => (i === group ? [...g, spec] : g)) };
}

export function replaceCondition(state: BiFilterState, at: ConditionAt, spec: BiFilterSpec): BiFilterState {
  if (at.group === undefined) return { ...state, filters: state.filters.map((f, i) => (i === at.index ? spec : f)) };
  return { ...state, anyOf: state.anyOf.map((g, gi) => (gi === at.group ? g.map((f, i) => (i === at.index ? spec : f)) : g)) };
}

export function removeCondition(state: BiFilterState, at: ConditionAt): BiFilterState {
  if (at.group === undefined) return { ...state, filters: state.filters.filter((_, i) => i !== at.index) };
  return { ...state, anyOf: state.anyOf.map((g, gi) => (gi === at.group ? g.filter((_, i) => i !== at.index) : g)).filter((g) => g.length > 0) };
}

export function addHaving(state: BiFilterState, spec: BiHavingSpec): BiFilterState {
  return state.having.length >= FILTER_LIMITS.maxHaving ? state : { ...state, having: [...state.having, spec] };
}

export function replaceHaving(state: BiFilterState, index: number, spec: BiHavingSpec): BiFilterState {
  return { ...state, having: state.having.map((h, i) => (i === index ? spec : h)) };
}

export function removeHaving(state: BiFilterState, index: number): BiFilterState {
  return { ...state, having: state.having.filter((_, i) => i !== index) };
}

/** Move a condition from the AND list to its own new OR group (or back out): the builder's "OR" button. */
export function toggleGroupOf(state: BiFilterState, at: ConditionAt): BiFilterState {
  if (at.group === undefined) {
    const spec = state.filters[at.index];
    if (spec === undefined || state.anyOf.length >= FILTER_LIMITS.maxGroups) return state;
    return { ...state, filters: state.filters.filter((_, i) => i !== at.index), anyOf: [...state.anyOf, [spec]] };
  }
  const spec = state.anyOf[at.group]?.[at.index];
  if (spec === undefined) return state;
  return removeCondition({ ...state, filters: [...state.filters, spec] }, at);
}

// ───────────────────────────── bulk paste ─────────────────────────────

export interface BulkValues {
  readonly values: readonly string[];
  /** Entries past the cap, not kept. */
  readonly overflow: number;
  /** Entries that were repeated. */
  readonly duplicates: number;
}

/**
 * A pasted list of ids or plates: one per line, or separated by commas, semicolons, tabs or spaces; trimmed, de-duplicated, at most `max`
 * values (the server's cap). `existing` are the values already chosen, so a second paste adds to them.
 */
export function parseBulkValues(text: string, existing: readonly string[] = [], max: number = FILTER_LIMITS.maxValues): BulkValues {
  const seen = new Set(existing);
  const values = [...existing];
  let overflow = 0;
  let duplicates = 0;
  // Lines, commas, semicolons and tabs separate entries. A list on ONE line with none of them is split on spaces (a row of plates); a line
  // with a separator keeps the spaces inside its entries (a reference can hold one).
  const separated = /[\n\r,;\t]/.test(text.trim());
  for (const raw of text.split(separated ? /[\n\r,;\t]+/ : /\s+/)) {
    const part = raw.trim();
    if (part === '') continue;
    if (seen.has(part)) {
      duplicates += 1;
    } else if (values.length >= max) {
      overflow += 1;
    } else {
      seen.add(part);
      values.push(part);
    }
  }
  return { values, overflow, duplicates };
}
